/**
 * Couples sticky sidebars to the main scroll container.
 *
 * Forwards wheel events from a sidebar to the main scroller only while the
 * header above the tabs is still visible. Once the sidebar is stuck at its
 * sticky top (header fully out), wheel events stop chaining so the main
 * content doesn't scroll along with the sidebar. Also dispatches a synthetic
 * scroll event when a sidebar mounts/unmounts: the layout reflows but no
 * scroll/resize event fires, so the StickyScroll component would otherwise
 * keep stale state.
 *
 * Nothing here is transcript-specific; candidate for @tsmono/react/hooks.
 */

import { useEffect, type RefObject } from "react";

import { useLatestRef } from "@tsmono/react/hooks";

export interface SidebarScrollTarget {
  /** The sidebar's sticky scroll container. Assumed stable across renders. */
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** Offset at which the sidebar sticks (px from the scroller top). Read at
   *  wheel time, so changes take effect without re-attaching listeners. */
  stickyTop: number;
  /** Identity token that changes when the sidebar mounts/unmounts
   *  (conditional render), forcing listener re-attachment and a sticky
   *  re-measure. */
  remountKey: string | number | boolean | null | undefined;
}

export interface UseSidebarScrollCouplingOptions {
  /** The main scroll container. */
  mainScrollRef: RefObject<HTMLDivElement | null>;
  /** Sidebars to couple. Pass a fresh array each render — re-attachment is
   *  driven by the remount keys, not array identity. */
  sidebars: ReadonlyArray<SidebarScrollTarget>;
}

export function useSidebarScrollCoupling(
  options: UseSidebarScrollCouplingOptions
): void {
  const { mainScrollRef, sidebars } = options;

  // Mirror the latest targets so wheel handlers read current sticky offsets.
  const sidebarsRef = useLatestRef(sidebars);

  // Value-stable re-attachment signal: changes only when a sidebar's remount
  // key does, never when offsets do.
  const structureKey = sidebars.map((s) => String(s.remountKey)).join("|");

  // Synthetic scroll on sidebar mount/unmount, after the DOM has settled.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      el.dispatchEvent(new Event("scroll"));
    }, 0);
    return () => clearTimeout(timer);
  }, [structureKey, mainScrollRef]);

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const main = mainScrollRef.current;
    if (!main) return;

    const makeHandler =
      (sidebar: HTMLDivElement, index: number) => (e: WheelEvent) => {
        const mainMaxTop = main.scrollHeight - main.clientHeight;
        // Is the sidebar currently stuck at its sticky top? If so, the header
        // above the tabs has already scrolled off — don't chain further main
        // scrolling or the main content itself would move with the sidebar.
        const mainRect = main.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();
        const sidebarTopInScroller = sidebarRect.top - mainRect.top;
        const stickyTop = sidebarsRef.current[index]?.stickyTop ?? 0;
        const sidebarIsSticky = sidebarTopInScroller <= stickyTop + 1;

        if (!sidebarIsSticky) {
          // Header still visible — forward all wheel input to the main
          // scroller so that the header collapses/expands. Suppress the
          // sidebar's default scroll for this step.
          const canMain =
            (e.deltaY > 0 && main.scrollTop < mainMaxTop - 0.5) ||
            (e.deltaY < 0 && main.scrollTop > 0.5);
          if (canMain) {
            e.preventDefault();
            main.scrollBy({ top: e.deltaY, behavior: "auto" });
          }
        } else if (
          e.deltaY < 0 &&
          sidebar.scrollTop <= 0 &&
          main.scrollTop > 0
        ) {
          // Sidebar is sticky and already at its own top — wheeling up should
          // bring the header back, so forward to main.
          e.preventDefault();
          main.scrollBy({ top: e.deltaY, behavior: "auto" });
        }
        // Otherwise let the sidebar's native wheel scroll proceed.
      };

    const entries = sidebarsRef.current.flatMap((target, index) => {
      const el = target.scrollRef?.current;
      if (!el) return [];
      const handler = makeHandler(el, index);
      // passive: false so we can preventDefault when taking over the scroll.
      el.addEventListener("wheel", handler, { passive: false });
      return [{ el, handler }];
    });
    return () => {
      for (const { el, handler } of entries) {
        el.removeEventListener("wheel", handler);
      }
    };
  }, [mainScrollRef, structureKey, sidebarsRef]);
}
